import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  alpha,
  Tabs,
  Tab,
  Divider,
  useTheme,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EventIcon from '@mui/icons-material/Event';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import NotesIcon from '@mui/icons-material/Notes';
import { palette, typography, theme as appTheme } from '../../theme/colors';
import { useCustomerHistory, useAddCustomerNote, useDeleteCustomerNote } from '../../hooks/useCustomerHistory';
import { CustomerNotesPanel } from './CustomerNotesPanel';
import { Customer } from '../../types/customer';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-history-tabpanel-${index}`}
      aria-labelledby={`customer-history-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface CustomerHistoryViewProps {
  customerId: string;
  customer?: Customer;
  currencySymbol?: string;
}

export const CustomerHistoryView = ({
  customerId,
  customer,
  currencySymbol = 'DZD ',
}: CustomerHistoryViewProps) => {
  const muiTheme = useTheme();
  const mode = muiTheme.palette.mode;

  const [activeTab, setActiveTab] = useState(0);

  // React Query hooks
  const { data: historyData, isLoading, error: historyError } = useCustomerHistory(customerId);
  const addNoteMutation = useAddCustomerNote();
  const deleteNoteMutation = useDeleteCustomerNote();

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleAddNote = async (note: string, isInternal: boolean) => {
    await addNoteMutation.mutateAsync({
      customerId,
      note,
      isInternal,
    });
  };

  const handleDeleteNote = async (noteId: string) => {
    await deleteNoteMutation.mutateAsync({
      customerId,
      noteId,
    });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress size={48} sx={{ color: palette.primary }} />
      </Box>
    );
  }

  if (historyError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {historyError instanceof Error ? historyError.message : 'Failed to load customer history'}
        </Alert>
      </Box>
    );
  }

  if (!historyData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No customer history data found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Customer Header (only if customer data provided) */}
      {customer && (
        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: appTheme.radius.md,
            mb: 3,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: appTheme.radius.md,
                  background: `linear-gradient(135deg, ${palette.primary} 0%, ${alpha(palette.primary, 0.7)} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 16px ${alpha(palette.primary, 0.3)}`,
                  flexShrink: 0,
                }}
              >
                <PersonIcon sx={{ color: '#FFF', fontSize: 32 }} />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.extrabold,
                    color: colors.textPrimary,
                    mb: 1,
                  }}
                >
                  {customer.firstName} {customer.lastName}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.textSecondary,
                      fontSize: typography.fontSize.sm,
                    }}
                  >
                    {customer.email}
                  </Typography>
                  {customer.phone && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.textSecondary,
                        fontSize: typography.fontSize.sm,
                      }}
                    >
                      {customer.phone}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.textMuted,
                    fontSize: typography.fontSize.xs,
                  }}
                >
                  Customer since {formatDate(customer.createdAt)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3, borderColor: colors.borderLight }} />

            {/* Summary Stats */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      letterSpacing: typography.letterSpacing.wide,
                      display: 'block',
                      mb: 1,
                    }}
                  >
                    LIFETIME VALUE
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: typography.fontFamily.display,
                      fontWeight: typography.fontWeight.bold,
                      color: palette.primary,
                    }}
                  >
                    {currencySymbol}{formatPrice(historyData.lifetimeValue)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      letterSpacing: typography.letterSpacing.wide,
                      display: 'block',
                      mb: 1,
                    }}
                  >
                    TOTAL ORDERS
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: typography.fontFamily.display,
                      fontWeight: typography.fontWeight.bold,
                      color: colors.textPrimary,
                    }}
                  >
                    {historyData.totalOrders}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      letterSpacing: typography.letterSpacing.wide,
                      display: 'block',
                      mb: 1,
                    }}
                  >
                    LOYALTY POINTS
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: typography.fontFamily.display,
                      fontWeight: typography.fontWeight.bold,
                      color: colors.textPrimary,
                    }}
                  >
                    {historyData.loyalty?.points || 0}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: appTheme.radius.md,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: colors.borderLight }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                color: colors.textSecondary,
                fontFamily: typography.fontFamily.body,
                fontWeight: typography.fontWeight.semibold,
                fontSize: typography.fontSize.sm,
                textTransform: 'none',
                minHeight: 56,
              },
              '& .Mui-selected': {
                color: palette.primary,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: palette.primary,
              },
            }}
          >
            <Tab
              icon={<ShoppingBagIcon />}
              iconPosition="start"
              label={`Orders (${historyData.orders.length})`}
            />
            <Tab
              icon={<ReceiptIcon />}
              iconPosition="start"
              label={`Sales (${historyData.sales.length})`}
            />
            <Tab
              icon={<EventIcon />}
              iconPosition="start"
              label={`Reservations (${historyData.reservations.length})`}
            />
            <Tab
              icon={<RequestQuoteIcon />}
              iconPosition="start"
              label={`Quotes (${historyData.quotes.length})`}
            />
            <Tab
              icon={<DescriptionIcon />}
              iconPosition="start"
              label={`Invoices (${historyData.invoices.length})`}
            />
            <Tab
              icon={<LocalShippingIcon />}
              iconPosition="start"
              label={`Deliveries (${historyData.deliveries.length})`}
            />
            <Tab
              icon={<LoyaltyIcon />}
              iconPosition="start"
              label="Loyalty"
            />
            <Tab
              icon={<NotesIcon />}
              iconPosition="start"
              label={`Notes (${historyData.notes.length})`}
            />
          </Tabs>
        </Box>

        <CardContent>
          {/* Orders Tab */}
          <TabPanel value={activeTab} index={0}>
            {historyData.orders.length === 0 ? (
              <Typography color={colors.textSecondary}>No orders found</Typography>
            ) : (
              <Box>
                {historyData.orders.map((order) => (
                  <Box
                    key={order.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: colors.surfaceElevated,
                      borderRadius: appTheme.radius.sm,
                      border: `1px solid ${colors.borderLight}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color={colors.textPrimary}>
                        {order.trackingNumber || order.id.slice(0, 8)}
                      </Typography>
                      <Chip
                        label={order.paymentStatus}
                        size="small"
                        sx={{
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color={colors.textSecondary}>
                      {formatDate(order.createdAt)} • {currencySymbol}{formatPrice(order.totalAmount)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </TabPanel>

          {/* Sales Tab */}
          <TabPanel value={activeTab} index={1}>
            {historyData.sales.length === 0 ? (
              <Typography color={colors.textSecondary}>No sales found</Typography>
            ) : (
              <Box>
                {historyData.sales.map((sale) => (
                  <Box
                    key={sale.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: colors.surfaceElevated,
                      borderRadius: appTheme.radius.sm,
                      border: `1px solid ${colors.borderLight}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color={colors.textPrimary}>
                        {sale.receiptNumber || sale.id.slice(0, 8)}
                      </Typography>
                      <Chip
                        label={sale.status}
                        size="small"
                        sx={{
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color={colors.textSecondary}>
                      {formatDate(sale.createdAt)} • {currencySymbol}{formatPrice(sale.totalAmount)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </TabPanel>

          {/* Reservations Tab */}
          <TabPanel value={activeTab} index={2}>
            {historyData.reservations.length === 0 ? (
              <Typography color={colors.textSecondary}>No reservations found</Typography>
            ) : (
              <Box>
                {historyData.reservations.map((reservation) => (
                  <Box
                    key={reservation.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: colors.surfaceElevated,
                      borderRadius: appTheme.radius.sm,
                      border: `1px solid ${colors.borderLight}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color={colors.textPrimary}>
                        Reservation {reservation.id.slice(0, 8)}
                      </Typography>
                      <Chip
                        label={reservation.status}
                        size="small"
                        sx={{
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color={colors.textSecondary}>
                      {formatDate(reservation.createdAt)} • {currencySymbol}{formatPrice(reservation.totalAmount)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </TabPanel>

          {/* Quotes Tab */}
          <TabPanel value={activeTab} index={3}>
            {historyData.quotes.length === 0 ? (
              <Typography color={colors.textSecondary}>No quotes found</Typography>
            ) : (
              <Box>
                {historyData.quotes.map((quote) => (
                  <Box
                    key={quote.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: colors.surfaceElevated,
                      borderRadius: appTheme.radius.sm,
                      border: `1px solid ${colors.borderLight}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color={colors.textPrimary}>
                        Quote {quote.quoteNumber}
                      </Typography>
                      <Chip
                        label={quote.status}
                        size="small"
                        sx={{
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color={colors.textSecondary}>
                      {formatDate(quote.createdAt)} • {currencySymbol}{formatPrice(quote.total)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </TabPanel>

          {/* Invoices Tab */}
          <TabPanel value={activeTab} index={4}>
            {historyData.invoices.length === 0 ? (
              <Typography color={colors.textSecondary}>No invoices found</Typography>
            ) : (
              <Box>
                {historyData.invoices.map((invoice) => (
                  <Box
                    key={invoice.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: colors.surfaceElevated,
                      borderRadius: appTheme.radius.sm,
                      border: `1px solid ${colors.borderLight}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color={colors.textPrimary}>
                        Invoice {invoice.invoiceNumber}
                      </Typography>
                      <Chip
                        label={invoice.status}
                        size="small"
                        sx={{
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color={colors.textSecondary}>
                      {formatDate(invoice.createdAt)} • {currencySymbol}{formatPrice(invoice.total)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </TabPanel>

          {/* Deliveries Tab */}
          <TabPanel value={activeTab} index={5}>
            {historyData.deliveries.length === 0 ? (
              <Typography color={colors.textSecondary}>No deliveries found</Typography>
            ) : (
              <Box>
                {historyData.deliveries.map((delivery) => (
                  <Box
                    key={delivery.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: colors.surfaceElevated,
                      borderRadius: appTheme.radius.sm,
                      border: `1px solid ${colors.borderLight}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color={colors.textPrimary}>
                        Delivery {delivery.id.slice(0, 8)}
                      </Typography>
                      <Chip
                        label={delivery.status}
                        size="small"
                        sx={{
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color={colors.textSecondary}>
                      {formatDate(delivery.createdAt)} • {delivery.deliveryAddress?.city || 'Unknown'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </TabPanel>

          {/* Loyalty Tab */}
          <TabPanel value={activeTab} index={6}>
            {historyData.loyalty ? (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 3,
                        backgroundColor: colors.surfaceElevated,
                        borderRadius: appTheme.radius.sm,
                        border: `1px solid ${colors.borderLight}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          letterSpacing: typography.letterSpacing.wide,
                          display: 'block',
                          mb: 1,
                        }}
                      >
                        CURRENT POINTS
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontFamily: typography.fontFamily.display,
                          fontWeight: typography.fontWeight.bold,
                          color: palette.primary,
                        }}
                      >
                        {historyData.loyalty.points}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 3,
                        backgroundColor: colors.surfaceElevated,
                        borderRadius: appTheme.radius.sm,
                        border: `1px solid ${colors.borderLight}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          letterSpacing: typography.letterSpacing.wide,
                          display: 'block',
                          mb: 1,
                        }}
                      >
                        LIFETIME POINTS
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontFamily: typography.fontFamily.display,
                          fontWeight: typography.fontWeight.bold,
                          color: colors.textPrimary,
                        }}
                      >
                        {historyData.loyalty.lifetimePoints}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Typography color={colors.textSecondary}>No loyalty account found</Typography>
            )}
          </TabPanel>

          {/* Notes Tab */}
          <TabPanel value={activeTab} index={7}>
            <CustomerNotesPanel
              customerId={customerId}
              notes={historyData.notes}
              onAddNote={handleAddNote}
              onDeleteNote={handleDeleteNote}
              isAdding={addNoteMutation.isPending}
              isDeleting={deleteNoteMutation.isPending ? deleteNoteMutation.variables?.noteId || null : null}
            />
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomerHistoryView;
